export const blogs = [
  {
    id: "1",
    title: "What is useState in React?",
    description: "A deep dive into the useState hook in React.",
    content: `
### What is useState?

\`useState\` is a Hook that allows you to have state variables in functional components.

**Example:**
\`\`\`js
const [count, setCount] = useState(0);
\`\`\`

**Interview Tip**: You can initialize with any value (array, object, number, etc.)

### Common Interview Questions:
- How does React know when to re-render?
- Can we use multiple useState calls in one component?
- Is useState asynchronous?
    `,
    image: "https://reactjs.org/logo-og.png",
    date: "2025-06-29"
  },
  {
    id: "2",
    title: "Understanding useEffect",
    description: "Learn how and why useEffect is used in React.",
    content: `
### What is useEffect?

\`useEffect\` lets you run side effects in function components.

**Common uses:**
- Fetching API data
- Setting up subscriptions
- Updating the DOM manually

**Example:**
\`\`\`js
useEffect(() => {
  console.log("Component mounted");
}, []);
\`\`\`

**Interview Questions:**
- Difference between useEffect and useLayoutEffect?
- How does cleanup work in useEffect?
- When does useEffect run again?
    `,
    image: "https://tse1.mm.bing.net/th/id/OIP.pmDh1JslF1RQCRM_wDgjdQHaD4?pid=Api&P=0&h=180",
    date: "2025-06-28"
  },
  {
    id: "3",
    title: "What is JSX in React?",
    description: "Explaining JSX and how it differs from HTML.",
    content: `
### What is JSX?

JSX is a syntax extension for JavaScript. It looks like HTML but compiles to \`React.createElement()\`.

**Example:**
\`\`\`js
const element = <h1>Hello, world!</h1>;
\`\`\`

### Common Questions:
- Why is JSX used in React?
- How is JSX compiled?
- Can we use expressions in JSX?
    `,
    image: "https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg",
    date: "2025-06-27"
  },
  {
    id: "4",
    title: "Difference between Props and State",
    description: "Key differences between props and state in React.",
    content: `
### Props vs State

**Props** are inputs to a component; **State** is local and mutable.

**Example:**
\`\`\`js
function Welcome(props) {
  return <h1>Hello, {props.name}</h1>;
}
\`\`\`

### Questions:
- When to use props over state?
- Can state be passed as props?
- Is state immutable?
    `,
    image: "https://tse2.mm.bing.net/th/id/OIP.jTs8Y_FeIvyuibZIVvTmHwHaEJ?pid=Api&P=0&h=180",
    date: "2025-06-26"
  },
  {
    id: "5",
    title: "What is Virtual DOM?",
    description: "Understanding Virtual DOM and how React uses it.",
    content: `
### Virtual DOM

Virtual DOM is a lightweight copy of the real DOM. React uses it to optimize UI rendering.

### Interview Questions:
- How does the virtual DOM improve performance?
- What is reconciliation?
- When is the real DOM updated?
    `,
    image: "https://tse4.mm.bing.net/th/id/OIP.4ANQt_wmeYwGw6d7gSjbRgHaDB?pid=Api&P=0&h=180",
    date: "2025-06-25"
  },
  {
    id: "6",
    title: "Controlled vs Uncontrolled Components",
    description: "What are controlled and uncontrolled components in forms?",
    content: `
### Controlled Component

State is handled by React.

**Example:**
\`\`\`js
<input value={value} onChange={handleChange} />
\`\`\`

### Uncontrolled Component

Uses DOM references instead.

**Example:**
\`\`\`js
<input ref={inputRef} />
\`\`\`

### Questions:
- Which is better for form validation?
- Can you mix both in one form?
    `,
    image: "https://tse4.mm.bing.net/th/id/OIP.8dJ40fUTBIzIptnUZXzcJwHaEK?pid=Api&P=0&h=180",
    date: "2025-06-24"
  },
  {
    id: "7",
    title: "React Lifecycle Methods vs Hooks",
    description: "Mapping old class lifecycle methods to hooks.",
    content: `
### Lifecycle to Hook Mapping

- \`componentDidMount\` → \`useEffect(() => {}, [])\`
- \`componentDidUpdate\` → \`useEffect(() => {})\`
- \`componentWillUnmount\` → \`return () => {}\` inside useEffect

### Questions:
- Why were class lifecycle methods replaced?
- Can you simulate shouldComponentUpdate with hooks?
    `,
    image: "https://tse4.mm.bing.net/th/id/OIP.3OjFCVYcQ_osARUBK6T0cQHaEK?pid=Api&P=0&h=180",
    date: "2025-06-23"
  },
  {
    id: "8",
    title: "React Keys in Lists",
    description: "Why React needs keys in list rendering.",
    content: `
### What are keys?

Keys help React identify which items changed.

\`\`\`js
{items.map(item => <li key={item.id}>{item.name}</li>)}
\`\`\`

### Questions:
- What happens if you don’t use keys?
- Why should keys be unique and stable?
    `,
    image: "https://tse4.mm.bing.net/th/id/OIP.tFBZ7v3z2oIfaubTS0_hKQHaDt?pid=Api&P=0&h=180",
    date: "2025-06-22"
  },
  {
    id: "9",
    title: "React Router Basics",
    description: "How routing works in React using React Router.",
    content: `
### Basic Setup

\`\`\`js
<Route path="/about" element={<About />} />
\`\`\`

### Interview Questions:
- Difference between BrowserRouter and HashRouter?
- How does nested routing work?
    `,
    image: "https://tse2.mm.bing.net/th/id/OIP.nrk8Q-BGsVnXVLkLjMhhlgHaEK?pid=Api&P=0&h=180",
    date: "2025-06-21"
  },
  {
    id: "10",
    title: "What is useRef in React?",
    description: "Exploring the useRef hook for DOM and values.",
    content: `
### useRef

Used to persist values across renders or reference DOM elements.

\`\`\`js
const inputRef = useRef();
\`\`\`

### Questions:
- Difference between useRef and createRef?
- Can you use useRef for timers or state caching?
    `,
    image: "https://tse4.mm.bing.net/th/id/OIP.y0_1PSa91z9gh6YeMqD38wAAAA?pid=Api&P=0&h=180",
    date: "2025-06-20"
  },
    {
    id: "11",
    title: "React Context API Explained",
    description: "Avoid prop drilling using Context API.",
    content: `
### What is Context API?

Provides a way to share values like theme, user, or language across components without explicitly passing props.

**Usage:**
\`\`\`js
const MyContext = React.createContext();
<MyContext.Provider value={value}>...</MyContext.Provider>
\`\`\`

### Interview Questions:
- How does Context differ from Redux?
- Can context be nested?
- When should you avoid using context?
    `,
    image: "https://images.unsplash.com/photo-1556155092-490a1ba16284?auto=format&fit=crop&w=800&q=80",
    date: "2025-06-19"
  },
  {
    id: "12",
    title: "React Memo and useMemo",
    description: "Optimize performance using memoization.",
    content: `
### React.memo

Prevents re-rendering of a component if props haven't changed.

**useMemo**

Caches the result of a computation:
\`\`\`js
const memoValue = useMemo(() => expensiveCalc(a), [a]);
\`\`\`

### Interview Questions:
- Difference between useMemo and useCallback?
- When not to use memoization?
- Does useMemo run on every render?
    `,
    image: "https://tse3.mm.bing.net/th/id/OIP.Dua59iLYx2C0bxFnCotmVwHaEK?pid=Api&P=0&h=180",
    date: "2025-06-18"
  },
  {
    id: "13",
    title: "Error Boundaries in React",
    description: "Catch JavaScript errors in React components.",
    content: `
### What are Error Boundaries?

React components that catch errors in child components.

**Only class components can be error boundaries.**

**Example:**
\`\`\`js
componentDidCatch(error, info) {
  logError(error);
}
\`\`\`

### Questions:
- Can hooks be used for error boundaries?
- How do they improve UX?
    `,
    image: "https://images.unsplash.com/photo-1558021212-51b6ecfa0db9?auto=format&fit=crop&w=800&q=80",
    date: "2025-06-17"
  },
  {
    id: "14",
    title: "React Portals",
    description: "Render children into a different DOM node.",
    content: `
### What is a Portal?

Allows rendering components outside the DOM hierarchy of the parent.

**Example:**
\`\`\`js
ReactDOM.createPortal(child, container);
\`\`\`

### Questions:
- Use cases for portals?
- Do events bubble through portals?
    `,
    image: "https://tse4.mm.bing.net/th/id/OIP.nJ939JfkiMTaugr8HHUnngAAAA?pid=Api&P=0&h=180",
    date: "2025-06-16"
  },
  {
    id: "15",
    title: "React Suspense and Lazy",
    description: "Code-splitting with Suspense and React.lazy.",
    content: `
### React.lazy

Dynamically import components.

**Example:**
\`\`\`js
const MyComponent = React.lazy(() => import('./MyComponent'));
\`\`\`

**Suspense**

Wrap lazy components:
\`\`\`js
<Suspense fallback={<div>Loading...</div>}>
  <MyComponent />
</Suspense>
\`\`\`

### Questions:
- How does lazy help in performance?
- Can you nest multiple Suspense components?
    `,
    image: "https://tse4.mm.bing.net/th/id/OIP.dKEhhjSKIzp1B3bVQbc28AHaEK?pid=Api&P=0&h=180",
    date: "2025-06-15"
  },
  {
    id: "16",
    title: "React Forms with Formik",
    description: "Simplify form management in React.",
    content: `
### Why Formik?

Reduces boilerplate in form handling and validation.

**Example:**
\`\`\`js
useFormik({ initialValues, onSubmit, validate })
\`\`\`

### Questions:
- Compare Formik with React Hook Form.
- How to handle custom validations?
    `,
    image: "https://tse4.mm.bing.net/th/id/OIP.zN8iJ2YKklpdXOxQnWempQHaEK?pid=Api&P=0&h=180",
    date: "2025-06-14"
  },
  {
    id: "17",
    title: "React Testing Library Basics",
    description: "Test UI behavior, not internals.",
    content: `
### React Testing Library

Focuses on user interaction, not implementation details.

**Example:**
\`\`\`js
render(<Button />);
fireEvent.click(screen.getByText('Click Me'));
\`\`\`

### Questions:
- Why avoid Enzyme?
- How to mock async requests?
    `,
    image: "https://tse1.mm.bing.net/th/id/OIP.g1Trv2IEiNYWwJuM9Hg_SAHaEK?pid=Api&P=0&h=180",
    date: "2025-06-13"
  },
  {
    id: "18",
    title: "React Performance Optimization",
    description: "Tips to boost React app speed.",
    content: `
### Key Tips:
- Use keys correctly
- Code split with lazy
- Avoid inline functions
- Memoize values

### Questions:
- What slows React apps down?
- How does reconciliation affect performance?
    `,
    image: "https://tse1.mm.bing.net/th/id/OIP.vyA4o8vChkhGY7zdhOfDYAHaEK?pid=Api&P=0&h=180",
    date: "2025-06-12"
  },
  {
    id: "19",
    title: "React with TypeScript",
    description: "Type-safe React applications.",
    content: `
### Benefits

- Better tooling
- Catch bugs early
- Predictable props

**Example:**
\`\`\`ts
type Props = { title: string };
const Header: React.FC<Props> = ({ title }) => <h1>{title}</h1>;
\`\`\`

### Questions:
- How to type functional components?
- TypeScript vs PropTypes?
    `,
    image: "https://tse3.mm.bing.net/th/id/OIP.csOYOC8bzKm0ws9YNQFiVAHaES?pid=Api&P=0&h=180",
    date: "2025-06-11"
  },
  {
    id: "20",
    title: "React and Redux Integration",
    description: "Manage global state with Redux.",
    content: `
### Redux Flow

Actions → Reducers → Store → Components

**Example:**
\`\`\`js
const counterReducer = (state = 0, action) => {
  switch (action.type) {
    case "INCREMENT": return state + 1;
    default: return state;
  }
}
\`\`\`

### Questions:
- When to use Redux?
- Difference between Redux and Context?
    `,
    image: "https://tse3.mm.bing.net/th/id/OIP.RMA71987s8gPifJ5SWu4qAHaEK?pid=Api&P=0&h=180",
    date: "2025-06-10"
  }

  // Add more blogs (11 to 20) if needed — do you want me to continue filling them now?
];
